import { LitElement, html, css, nothing } from "lit";
import { reiterStyles } from "../styles/reiter-styles.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { customElement, property, state } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";

import { registerStyles } from "../styles/register-styles.js";

/** Ein Abschnitt der Seite: die `id` der `<section>`, der Name als Substantiv. */
export interface Abschnitt {
  id: string;
  name: string;
}

/** Der Wert, den die Leiste am Dokument hinterlässt — für `scroll-margin-top` der Sections. */
export const ABSCHNITTE_HOEHE_VAR = "--portal-abschnitte-hoehe";

/** Erst ab so vielen Abschnitten lohnt sich eine Leiste. */
const MINDESTENS = 3;

/**
 * Die Abschnittsleiste — „Auf dieser Seite" (KONZEPT-navigation.md).
 *
 * Die zweite Ebene der Navigation, sichtbar anders als die erste: ein
 * Register in Mono-Versalien unter dem Seitenkopf, mitlaufend, mit dem
 * aktiven Abschnitt hervorgehoben. Das Hauptmenü bleibt die Landkarte des
 * Portals und überall gleich; diese Leiste beantwortet nur „wo bin ich auf
 * dieser Seite, was kommt noch".
 *
 * DIE SEITE REGISTRIERT NICHTS. Sie übergibt `{ id, name }` — die Leiste
 * findet die `<section id>`-Elemente selbst im Shadow Root der Seite, beim
 * ersten Rendern und nach jeder Änderung der Liste (die Seiten rendern
 * datenabhängig; ein Abschnitt kann später kommen oder fehlen). Was es nicht
 * gibt, steht nicht in der Leiste; unter drei Abschnitten gibt es keine.
 *
 * Kein Framework-Ereignis, kein Scroll-Handler: ein `IntersectionObserver`
 * je Element. Klick scrollt und setzt den HASH per `replaceState` —
 * teilbar, aber ohne Verlaufseintrag je Sprung (sonst wäre der
 * Zurück-Button nach fünf Sprüngen unbrauchbar). Kommt die Seite mit einem
 * Hash, springt die Leiste dorthin, sobald das Element da ist.
 *
 * Auf Mobil dieselbe Zeile, waagerecht scrollbar — kein Burger.
 */
@customElement("app-abschnitte")
@localized()
export class AppAbschnitte extends LitElement {
  @property({ attribute: false }) abschnitte: Abschnitt[] = [];

  /** Die gefundenen Abschnitte — nur die mit Element. */
  @state() private _vorhanden: Array<Abschnitt & { el: HTMLElement }> = [];

  @state() private _aktiv = "";

  private _observer?: IntersectionObserver;
  private _hoehenObserver?: ResizeObserver;
  /** Der Hash der Adresse ist einmal einzulösen — sobald sein Element da ist. */
  private _hashOffen = true;

  static override styles = [
    reiterStyles,
    registerStyles,
    css`
      :host {
        display: block;
        position: sticky;
        top: var(--pd-site-header-height, 90px);
        z-index: 4;
        background: var(--portal-papier);
      }
      nav {
        max-width: var(--portal-content-width);
        margin: 0 auto;
        padding: 0 var(--portal-gutter);
      }
    `,
  ];

  override connectedCallback(): void {
    super.connectedCallback();
    this._hoehenObserver = new ResizeObserver(() => this._meldeHoehe());
    this._hoehenObserver.observe(this);
  }

  override disconnectedCallback(): void {
    this._observer?.disconnect();
    this._hoehenObserver?.disconnect();
    document.documentElement.style.setProperty(ABSCHNITTE_HOEHE_VAR, "0px");
    super.disconnectedCallback();
  }

  override updated(): void {
    this._suche();
  }

  /** Die Höhe der Leiste am Dokument — die Sections rechnen sie in ihren Sprungabstand ein. */
  private _meldeHoehe(): void {
    const h = this._vorhanden.length >= MINDESTENS ? this.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty(ABSCHNITTE_HOEHE_VAR, `${Math.round(h)}px`);
  }

  /** Die Elemente zu den IDs — im Shadow Root der Seite, in der die Leiste steht. */
  private _suche(): void {
    const wurzel = this.getRootNode() as ShadowRoot | Document;
    const gefunden = this.abschnitte
      .map((a) => ({ ...a, el: wurzel.getElementById(a.id) as HTMLElement | null }))
      .filter((a): a is Abschnitt & { el: HTMLElement } => a.el !== null);
    const gleich =
      gefunden.length === this._vorhanden.length &&
      gefunden.every((a, i) => a.el === this._vorhanden[i]?.el);
    if (gleich) {
      this._loeseHashEin();
      return;
    }
    this._vorhanden = gefunden;
    this._beobachte();
    this._meldeHoehe();
    this._loeseHashEin();
  }

  private _beobachte(): void {
    this._observer?.disconnect();
    if (this._vorhanden.length < MINDESTENS) return;
    // Aktiv ist der oberste Abschnitt, der die obere Bildhälfte berührt.
    const kopf = this._kopfHoehe();
    this._observer = new IntersectionObserver(() => this._bestimmeAktiv(), {
      rootMargin: `-${kopf}px 0px -55% 0px`,
      threshold: [0, 0.01, 0.5, 1],
    });
    for (const a of this._vorhanden) this._observer.observe(a.el);
    this._bestimmeAktiv();
  }

  private _kopfHoehe(): number {
    const stil = getComputedStyle(document.documentElement);
    const header = Number.parseFloat(stil.getPropertyValue("--pd-site-header-height")) || 90;
    return header + this.getBoundingClientRect().height;
  }

  /** Der letzte Abschnitt, dessen Anfang über der Lesekante liegt. */
  private _bestimmeAktiv(): void {
    const kante = this._kopfHoehe() + 8;
    let aktiv = this._vorhanden[0]?.id ?? "";
    for (const a of this._vorhanden) {
      if (a.el.getBoundingClientRect().top <= kante) aktiv = a.id;
    }
    if (aktiv !== this._aktiv) {
      this._aktiv = aktiv;
      this._zeigeAktiven();
    }
  }

  /** Auf Mobil den aktiven Eintrag in der scrollbaren Zeile sichtbar halten. */
  private _zeigeAktiven(): void {
    this.updateComplete.then(() => {
      const el = this.shadowRoot?.querySelector<HTMLElement>('a[aria-current="location"]');
      el?.scrollIntoView({ inline: "nearest", block: "nearest", behavior: "auto" });
    });
  }

  private _loeseHashEin(): void {
    if (!this._hashOffen) return;
    const id = decodeURIComponent(window.location.hash.replace(/^#/, ""));
    if (!id) {
      this._hashOffen = false;
      return;
    }
    const ziel = this._vorhanden.find((a) => a.id === id);
    if (!ziel) return; // Element kommt vielleicht noch — offen lassen.
    this._hashOffen = false;
    this._springe(ziel.el, "auto");
  }

  private _springe(el: HTMLElement, verhalten?: ScrollBehavior): void {
    const reduziert = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ block: "start", behavior: verhalten ?? (reduziert ? "auto" : "smooth") });
  }

  private _klick(e: Event, a: Abschnitt & { el: HTMLElement }): void {
    e.preventDefault();
    this._springe(a.el);
    // replaceState, nicht pushState: teilbar, aber kein Verlaufseintrag je Sprung.
    window.history.replaceState(window.history.state, "", `#${a.id}`);
    this._aktiv = a.id;
  }

  override render() {
    if (this._vorhanden.length < MINDESTENS) return nothing;
    return html`
      <nav aria-label=${msg("Auf dieser Seite", { id: "app.abschnitte.label" })}>
        <ol class="reiter">
          ${this._vorhanden.map(
            (a) =>
              html`<li>
                <a
                  href=${`#${a.id}`}
                  aria-current=${ifDefined(this._aktiv === a.id ? "location" : undefined)}
                  @click=${(e: Event) => this._klick(e, a)}
                  >${a.name}</a
                >
              </li>`,
          )}
        </ol>
      </nav>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-abschnitte": AppAbschnitte;
  }
}
